import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Dropdown} from '@/components/atoms';
import {
  PageHeader,
  TargetTemplateUpload,
  TextInputField
} from '@/components/molecules';
import {PageContainer} from '@/components/organisms';
import {useDropdown} from '@/hooks/Dropdown-hook';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  perfectStoreInitialFormData,
  resetPerfectStoreFormData,
  setPerfectStoreFormData
} from '@/store/slices/perfectStoreSlice';
import {FONT_SIZE} from '@/utils/constants/globalStyle';
import {Constants} from '@/utils/constants/commonConstants';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';
import {usePerfectStore} from '@/hooks/usePerfectStore';
import {
  FIRST_POSITION_CSV_RULES,
  PEPIRIS_CSV_RULES,
  PEPIRIS_TASK_NAME_OPTIONS,
  PEPIRIS_TASK_NAMES,
  isFirstPositionTarget,
  isStoreExecutionPepIrisTarget
} from '@/utils/constants/perfectStoreConstants';
import {
  downloadPerfectStoreTemplate,
  isPerfectStoreActiveStartDateLocked,
  isPerfectStoreIdentityFieldsLocked
} from '@/helpers/perfectStoreUtils';
import AvailabilitySection from './AvailabilitySection';
import AdditionalOptionsSection from './AdditionalOptionsSection';
import {
  targetNameUiSchema,
  targetCoolerPurityUiSchema,
  minimumDistinctSkuUiSchema
} from './schemas';

const PerfectStoreSetUp = ({formData, setFormData}) => {
  const dispatch = useAppDispatch();
  const perfectStoreId = useAppSelector((state) => state?.perfectStore?.id);
  const perfectStoreStatus = useAppSelector(
    (state) => state?.perfectStore?.status
  );
  const identityFieldsLocked =
    isPerfectStoreIdentityFieldsLocked(perfectStoreStatus);
  const activeStartDateLocked =
    isPerfectStoreActiveStartDateLocked(perfectStoreStatus);
  const {onTemplateUpdate} = usePerfectStore();
  const {dropdownList: targetTypeOptions} = useDropdown({
    dropdownType: DROPDOWN_TYPES.PerfectStoreTargetType
  });

  const hasTargetType = !!formData?.targetType;
  const showFirstPositionSetup = isFirstPositionTarget(formData?.targetType);
  const showPepIrisSetup = isStoreExecutionPepIrisTarget(formData?.targetType);
  const showSetupSections = showFirstPositionSetup || showPepIrisSetup;

  const isCoolerPurityTask =
    formData?.taskName === PEPIRIS_TASK_NAMES.COOLER_PURITY;
  const isOutOfStockTask =
    formData?.taskName === PEPIRIS_TASK_NAMES.OUT_OF_STOCK_CE ||
    formData?.taskName === PEPIRIS_TASK_NAMES.OUT_OF_STOCK_CEDO_DO;

  const targetTypeDropdownValue = useMemo(() => {
    const current = formData?.targetType;
    if (!current) return [];
    const match = (targetTypeOptions || []).find(
      (opt) => opt.value === current || opt.displayText === current
    );
    return match
      ? [match]
      : [{id: current, displayText: current, value: current}];
  }, [formData?.targetType, targetTypeOptions]);

  const taskNameDropdownValue = useMemo(() => {
    const current = formData?.taskName;
    if (!current) return [];
    const match = PEPIRIS_TASK_NAME_OPTIONS.find(
      (opt) => opt.value === current
    );
    return match
      ? [match]
      : [{id: current, displayText: current, value: current}];
  }, [formData?.taskName]);

  const handleFilterData = useCallback(
    (data) => {
      setFormData((prev) => {
        const next = {...prev, ...data};
        dispatch(setPerfectStoreFormData(next));
        return next;
      });
    },
    [dispatch, setFormData]
  );

  const handleResetFormData = useCallback(
    (data) => {
      const defaultFormData = {
        ...perfectStoreInitialFormData,
        targetType: data?.targetType || ''
      };
      setFormData(defaultFormData);
      dispatch(resetPerfectStoreFormData(defaultFormData));
    },
    [dispatch, setFormData]
  );

  const handleTaskNameChange = useCallback(
    (val) => {
      const selected = val?.[0];
      const taskName = selected?.value ?? '';
      const updates = {
        taskName,
        targetCoolerPurity: '',
        targetCoolerPurityValid: undefined,
        minimumDistinctSku: '',
        minimumDistinctSkuValid: undefined
      };
      handleFilterData(updates);
    },
    [handleFilterData]
  );

  const handleTemplateDownload = useCallback(() => {
    return downloadPerfectStoreTemplate(formData?.targetType);
  }, [formData?.targetType]);

  return (
    <>
      <PageContainer data-testid='perfect-store-setup-step'>
        <PageHeader fontSize={FONT_SIZE.LG} title='Perfect Store Setup' />
        <div className='flex flex-row flex-wrap items-start gap-6 pb-4'>
          <div>
            <Dropdown
              key='perfect-store-target-type'
              label='Target Type *'
              value={targetTypeDropdownValue}
              updateValue={(val) => {
                const selectedType = val?.[0]?.value ?? '';
                if (selectedType === (formData?.targetType ?? '')) return;
                handleResetFormData({targetType: selectedType});
              }}
              isJsonWrap={false}
              childList={targetTypeOptions}
              className='dropdown-width text-sm'
              isDark={true}
              placeholder='Target Type'
              isClearable={true}
              selection='single'
              size='medium'
              disabled={identityFieldsLocked}
            />
          </div>
          {showPepIrisSetup && (
            <div>
              <Dropdown
                key='perfect-store-task-name'
                label='Task Name *'
                value={taskNameDropdownValue}
                updateValue={handleTaskNameChange}
                isJsonWrap={false}
                childList={PEPIRIS_TASK_NAME_OPTIONS}
                className='dropdown-width text-sm'
                isDark={true}
                placeholder='Task Name'
                isClearable={true}
                selection='single'
                size='medium'
              />
            </div>
          )}
          {hasTargetType && (
            <div className='max-w-[296px]'>
              <TextInputField
                value={formData?.name}
                updateValue={(val, isValid) => {
                  handleFilterData({
                    name: val,
                    nameValid: isValid
                  });
                }}
                label='Name *'
                schema={{type: 'string'}}
                uiSchema={{
                  ...targetNameUiSchema,
                  disabled: identityFieldsLocked
                }}
                disabled={identityFieldsLocked}
                visible={true}
              />
            </div>
          )}
          {showPepIrisSetup && isCoolerPurityTask && (
            <div className='max-w-[296px]'>
              <TextInputField
                value={formData?.targetCoolerPurity}
                updateValue={(val, isValid) => {
                  handleFilterData({
                    targetCoolerPurity: val,
                    targetCoolerPurityValid: isValid
                  });
                }}
                label='Target Cooler Purity *'
                schema={{type: 'number'}}
                uiSchema={targetCoolerPurityUiSchema}
                visible={true}
                suffix='%'
              />
            </div>
          )}
          {showPepIrisSetup && isOutOfStockTask && (
            <div className='max-w-[296px]'>
              <TextInputField
                value={formData?.minimumDistinctSku}
                updateValue={(val, isValid) => {
                  handleFilterData({
                    minimumDistinctSku: val,
                    minimumDistinctSkuValid: isValid
                  });
                }}
                label='Minimum Number of Distinct SKU *'
                schema={{type: 'number'}}
                uiSchema={minimumDistinctSkuUiSchema}
                visible={true}
              />
            </div>
          )}
        </div>

        {showFirstPositionSetup && (
          <>
            <p className='mb-4 mt-2 text-lg font-bold'>
              First Position Store Target
            </p>
            <TargetTemplateUpload
              formData={formData}
              setFormData={setFormData}
              handleDownloadOverride={handleTemplateDownload}
              uploadModalTitle='Upload Store Target List'
              uploadModalSubTitle='Upload Store Target file'
              isCsvValidationRequired={true}
              customRules={FIRST_POSITION_CSV_RULES}
              uploadButtonLabel='Upload Template'
              uploadFor={Constants.PerfectStore}
              onTemplateUpdate={perfectStoreId ? onTemplateUpdate : undefined}
              salesTargetId={perfectStoreId ?? undefined}
            />
          </>
        )}

        {showPepIrisSetup && (
          <>
            <p className='mb-4 mt-2 text-lg font-bold'>
              Store Execution Target - PepIris
            </p>
            <TargetTemplateUpload
              formData={formData}
              setFormData={setFormData}
              handleDownloadOverride={handleTemplateDownload}
              uploadModalTitle='Upload Store Target List'
              uploadModalSubTitle='Upload Store Target file'
              isCsvValidationRequired={true}
              customRules={PEPIRIS_CSV_RULES}
              uploadButtonLabel='Upload Template'
              uploadFor={Constants.PerfectStore}
              onTemplateUpdate={perfectStoreId ? onTemplateUpdate : undefined}
              salesTargetId={perfectStoreId ?? undefined}
            />
          </>
        )}
      </PageContainer>

      {showSetupSections && (
        <PageContainer data-testid='perfect-store-availability-card'>
          <PageHeader fontSize={FONT_SIZE.LG} title='Availability' />
          <AvailabilitySection
            formData={formData}
            onChange={handleFilterData}
            lockActiveStartDate={activeStartDateLocked}
            isPepIris={showPepIrisSetup}
          />
          <hr className='my-6 border-gray' />
          <p className='mb-4 text-lg font-bold'>Additional Options</p>
          <AdditionalOptionsSection
            formData={formData}
            onChange={handleFilterData}
            isPepIris={showPepIrisSetup}
          />
        </PageContainer>
      )}
    </>
  );
};

PerfectStoreSetUp.propTypes = {
  formData: PropTypes.object,
  setFormData: PropTypes.func.isRequired
};

export default PerfectStoreSetUp;
